import React from "react";
import ComplaintDateField from "./components/ComplaintDateField";
import ComplaintUploadField from "./components/ComplaintUploadField";
import useComplaintForm from "./hooks/useComplaintForm";

const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};
function getResponseCode(result) {
  return Number(result?.ResponseCode ?? result?.responseCode ?? 0);
}

function getReferenceId(result) {
  return result?.ReferenceID ?? result?.referenceID ?? "";
}

function HtmlListSummary({ title, items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="container-fluid formAlert">
        <h4>{title}</h4>
        <ul className="list-danger">
          {items.map((item, index) => (
            <li
              key={`${index}-${item}`}
              dangerouslySetInnerHTML={{ __html: item }}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function ValidationSummary({ title, items }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="container-fluid formAlert">
        <h4>{title}</h4>
        <ul className="list-danger">
          {items.map((item) => (
            <li key={item}>*{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function getCharactersRemaining(value, max) {
  return `Maximum characters ${max - String(value || "").length}`;
}

export default function ComplaintFormIsland({ config }) {
  const {
    form,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    recaptchaError,
    validationItems,
    updateField,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
  } = useComplaintForm(config);

  if (submitResult) {
    if (getResponseCode(submitResult) === 200) {
      return (
        <div className="resultContent">
          <div className="w-100 text-center mb-4">
            <h3 className="mb-3 text-bolder text-darkBlue">{config.labels.thankYouTitle}</h3>
            <h2 className="text-corporateBlue text-bolder">{form.firstName}</h2>
          </div>
          <div className="refNumber-cover blueBarTop">
            <div className="p-3">
              <h4 className="section-title">{config.labels.thankYouMessage}</h4>
              <div className="refNumber">
                <strong>{config.labels.referenceTitle}</strong>
                <h2>
                  <strong>{getReferenceId(submitResult)}</strong>
                </h2>
              </div>
            </div>
          </div>
          <div className="my-5 px-4 pt-3 pb-5 bg-lightest-blue">
            <div className="row">
              <div className="col-md-6">
                <h4
                  className="section-title text-corporateBlue"
                              dangerouslySetInnerHTML={{ __html: decodeHtml(config.thankYouContentTitleHtml) }}
                />
                <div
                  className="info"
                              dangerouslySetInnerHTML={{ __html: decodeHtml(config.thankYouContentHtml) }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="resultContent">
        <div className="w-100 text-center my-4">
          <h3 className="text-danger mb-3">{config.labels.errorFlowTitle}</h3>
          <h4 className="text-corporateBlue text-bolder">{config.labels.errorFlowSubtitle}</h4>
        </div>
        <div className="refNumber-cover blueBarTop">
          <div className="text-center">
            <h4 className="text-corporateBlue mt-5 mb-3">{config.labels.errorFlowDescription}</h4>
            <button
              type="button"
              className="btn btn-arrow-left btn-back"
              onClick={() => window.location.reload()}
            >
              <span>{config.labels.backLabel}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showPersonalDetailsError =
    fieldErrors.firstName || fieldErrors.surname || fieldErrors.email;
  const showDescriptionRequiredError =
    (fieldErrors.desc && form.desc.length === 0) ||
    (fieldErrors.yourIdeal && form.yourIdeal.length === 0) ||
    fieldErrors.when;
  const showDescriptionLengthError = fieldErrors.desc === "maxLength";
  const showIdealLengthError = fieldErrors.yourIdeal === "maxLength";

  return (
    <form onSubmit={submitForm}>
      <h1 className="text-bolder text-dark-blue my-4">{config.labels.pageTitle}</h1>

      <HtmlListSummary title={config.labels.serverValidationTitle} items={serverSideErrors} />
      <ValidationSummary title={config.labels.validationTitle} items={validationItems} />

      <div className="row">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title">{config.labels.personalDetailsTitle}</h4>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.firstName ? "is-invalid" : ""}`}>
            <label htmlFor="firstName">*{config.labels.firstNameLabel}</label>
            <div className="validation-cover">
              <input
                id="firstName"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.firstName}
                placeholder={config.labels.firstNamePlaceholder}
                onChange={(event) => updateField("firstName", event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-md-4">
          <div className={`form-group ${fieldErrors.surname ? "is-invalid" : ""}`}>
            <label htmlFor="surname">*{config.labels.surnameLabel}</label>
            <div className="validation-cover">
              <input
                id="surname"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.surname}
                placeholder={config.labels.surnamePlaceholder}
                onChange={(event) => updateField("surname", event.target.value)}
              />
            </div>
          </div>
        </div>
        {showPersonalDetailsError ? (
          <div className="col-md-4 mb-md-4 d-none d-md-block">
            <div className="text-danger mt-4 py-2">
              <i dangerouslySetInnerHTML={{ __html: `*${config.labels.personalDetailsErrorHtml}` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.email ? "is-invalid" : ""}`}>
            <label htmlFor="email">*{config.labels.emailLabel}</label>
            <div className="validation-cover">
              <input
                id="email"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.email}
                placeholder={config.labels.emailPlaceholder}
                onChange={(event) => updateField("email", event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className={`form-group ${fieldErrors.phone ? "is-invalid" : ""}`}>
            <label htmlFor="phone">{config.labels.telephoneLabel}</label>
            <div className="validation-cover">
              <input
                id="phone"
                type="text"
                className="form-control"
                autoComplete="off"
                value={form.phone}
                placeholder={config.labels.telephonePlaceholder}
                onChange={(event) => updateField("phone", event.target.value)}
              />
            </div>
          </div>
        </div>
        {showPersonalDetailsError ? (
          <div className="col-md-4 d-md-none mb-3">
            <div className="text-danger">
              <i dangerouslySetInnerHTML={{ __html: `*${config.labels.personalDetailsErrorHtml}` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title pb-2">{config.labels.detailsTitle}</h4>
          <div className="row">
            <div className="col-md-8">
              <p className="text-darkBlue mb-5">
                <i>{config.labels.detailsSubtitle}</i>
              </p>
            </div>
          </div>
          <div className="row mb-4">
            <div className="col-md-4 mb-mb-4">
              <div className="form-group">
                <label htmlFor="address">{config.labels.addressLabel}</label>
                <textarea
                  id="address"
                  className="form-control"
                  autoComplete="off"
                  rows="3"
                  value={form.address}
                  placeholder={config.labels.addressPlaceholder}
                  onChange={(event) => updateField("address", event.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4 mb-md-4">
              <div className={`form-group ${fieldErrors.mprn ? "is-invalid" : ""}`}>
                <label htmlFor="mprn">{config.labels.mprnLabel}</label>
                <div className="formHelper">
                  <div className="validation-cover">
                    <input
                      id="mprn"
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      value={form.mprn}
                      placeholder={config.labels.mprnPlaceholder}
                      onChange={(event) => updateField("mprn", event.target.value)}
                    />
                  </div>
                  <a href="javascript:void(0)" className="tooltip-toggle">
                    <i>{config.labels.tooltipLabel}</i>
                  </a>
                  <div className="tooltip">
                    <div className="tooltip-inner">{config.labels.tooltipDescription}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <div className="form-group">
            <label htmlFor="refNum" className="line-height-normal">
              {config.labels.existingRefLabel}
            </label>
            <input
              id="refNum"
              type="text"
              className="form-control"
              autoComplete="off"
              value={form.refNum}
              placeholder={config.labels.existingRefPlaceholder}
              onChange={(event) => updateField("refNum", event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-8">
              <div className={`form-group ${fieldErrors.desc ? "is-invalid" : ""}`}>
                <label htmlFor="desc">*{config.labels.descriptionLabel}</label>
                <div className="validation-cover">
                  <textarea
                    id="desc"
                    className="form-control"
                    autoComplete="off"
                    rows="10"
                    value={form.desc}
                    placeholder={config.labels.descriptionPlaceholder}
                    onChange={(event) => updateField("desc", event.target.value)}
                  />
                </div>
                <div
                  className={`text-right mt-2 small ${
                    form.desc.length > 4000 ? "text-danger" : "text-corporateBlue"
                  }`}
                >
                  <i>{getCharactersRemaining(form.desc, 4000)}</i>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              {showDescriptionRequiredError ? (
                <div className="text-danger mt-4">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.disclaimerErrorHtml}` }} />
                </div>
              ) : null}
              {showDescriptionLengthError ? (
                <div className="text-danger mt-4">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.descriptionLengthErrorHtml}` }} />
                </div>
              ) : null}
              {showIdealLengthError ? (
                <div className="text-danger mt-4">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.idealLengthErrorHtml}` }} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <ComplaintDateField
            id="when"
            label={config.labels.whenLabel}
            required
            error={fieldErrors.when}
            value={form.when}
            onChange={(value) => updateField("when", value)}
            placeholder={config.labels.whenPlaceholder}
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-8">
              <div className={`form-group ${fieldErrors.yourIdeal ? "is-invalid" : ""}`}>
                <label htmlFor="yourIdeal">*{config.labels.yourIdealLabel}</label>
                <div className="validation-cover">
                  <textarea
                    id="yourIdeal"
                    className="form-control"
                    autoComplete="off"
                    rows="10"
                    value={form.yourIdeal}
                    placeholder={config.labels.yourIdealPlaceholder}
                    onChange={(event) => updateField("yourIdeal", event.target.value)}
                  />
                </div>
                <div
                  className={`text-right mt-2 small ${
                    form.yourIdeal.length > 1000 ? "text-danger" : "text-corporateBlue"
                  }`}
                >
                  <i>{getCharactersRemaining(form.yourIdeal, 1000)}</i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop pb-5">
          <h4 className="section-title">{config.labels.documentationTitle}</h4>
          <p className="text-darkBlue mb-2">
            <i>
              <strong>{config.labels.documentationSubtitle}</strong>
            </i>
          </p>
          <p className="text-darkBlue">
            <i>{config.labels.documentationFileTypes}</i>
          </p>
          <div className="row position-relative">
            <div className="toast" style={{ position: "absolute", top: 0, right: "1rem" }} data-delay="5000">
              <div className="toast-header text-danger">
                <strong className="mr-auto">{config.labels.documentationAlertTitle}</strong>
                <button type="button" className="ml-2 mb-1 close" data-bs-dismiss="toast" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="toast-body text-danger">{config.labels.documentationAlert}</div>
            </div>
            <ComplaintUploadField
              fieldId="files"
              title={config.labels.documentationLabel}
              currentFiles={form.files}
              allowedExtensions={config.allowedExtensions}
              fileSizeLimit={config.fileSizeLimit}
              limitOfFileNumber={config.limitOfFileNumber}
              messages={config.uploadMessages}
              onUploadSuccess={addUploadedFile}
              onRemoveSuccess={removeFileByNameGuid}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container-fluid blueBarTop">
          <h4 className="section-title">{config.labels.disclaimerTitle}</h4>
          <div className="row">
            <div className="col-md-5">
              <div className={`form-group tandcCheckbox ${fieldErrors.tandc ? "is-invalid" : ""}`}>
                <div className="validation-cover">
                  <input
                    id="tandc"
                    type="checkbox"
                    checked={form.tandc}
                    onChange={(event) => updateField("tandc", event.target.checked)}
                  />
                  <label htmlFor="tandc">{config.labels.termsCheckboxLabel}</label>
                </div>
              </div>
            </div>
            {fieldErrors.tandc ? (
              <div className="col-md-4 d-flex align-items-center">
                <div className="text-danger">
                  <i dangerouslySetInnerHTML={{ __html: `*${config.labels.tandcErrorHtml}` }} />
                </div>
              </div>
            ) : null}
          </div>
          <div className="row">
            <div className="col-md-5">
              <div
                className="tandcDesc"
                dangerouslySetInnerHTML={{ __html: config.labels.termsDescriptionHtml }}
              />
            </div>
          </div>
          <div className={`form-group mt-5 ${recaptchaError ? "is-invalid" : ""}`}>
            <div className="validation-cover">
              <div id="react-island-recaptcha" />
            </div>
          </div>
        </div>
      </div>

      <div className="row my-4">
        <div className="col-6 col-md-3 offset-md-3 column mb-3 pl-0">
          <a href={config.cancelUrl} className="btn btn-wide btn-arrow-left btn-back">
            <span>{config.labels.cancelLabel}</span>
          </a>
        </div>
        <div className="col-6 col-md-3 column mb-3 pr-0">
          <button type="submit" className="btn btn-wide btn-arrow-right btn-next" disabled={loading}>
            <span>{loading ? config.labels.submittingLabel : config.labels.submitLabel}</span>
          </button>
        </div>
      </div>
    </form>
  );
}
